// Features signal — capabilities do browser/runtime que afetam UX.
//
// AVIF/WebP via createImageBitmap: detecta suporte real (não só presence
// de API). Tizen 4 reporta WebP como suportado mas decoder quebra em
// muitos GIFs animados — testamos com data: URI mínima.
//
// IntersectionObserver: alguns webOS 3.x têm IO mas com bugs raros.
// Em produção testamos por presence (suficiente pra MVP).

export interface FeaturesSignal {
  /** Browser decode AVIF? Ativa avif preferred em images quando suportado. */
  avif: boolean
  /** WebP? Mais comum (Tizen 4+, webOS 4+, Android 4.0+). */
  webp: boolean
  /** IntersectionObserver pra lazy load images/queries. */
  intersectionObserver: boolean
  /** Service Worker. Sem isso, cache de imagens vira só HTTP cache. */
  serviceWorker: boolean
  /** Web Worker pra parsing off-main-thread (snapshot parser). */
  webWorker: boolean
}

const DEFAULT_SIGNAL: FeaturesSignal = {
  avif: false,
  webp: false,
  intersectionObserver: false,
  serviceWorker: false,
  webWorker: false,
}

let _cached: FeaturesSignal | null = null

/** Lê features e cacheia (estável durante a sessão). */
export function readFeaturesSignal(): FeaturesSignal {
  if (_cached) return _cached
  if (typeof window === 'undefined') return DEFAULT_SIGNAL

  // Sync detections
  const intersectionObserver = typeof IntersectionObserver !== 'undefined'
  const serviceWorker = typeof navigator !== 'undefined' && 'serviceWorker' in navigator
  const webWorker = typeof Worker !== 'undefined'

  // Image format detection é async (createImageBitmap retorna Promise).
  // Fazemos lookup síncrono inicial via canvas.toDataURL(); calibração
  // mais precisa fica em pré-fetch async (não bloqueia boot).
  let webp = false
  let avif = false
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    // toDataURL retorna data:image/webp;base64,... se suportado
    webp = canvas.toDataURL('image/webp').startsWith('data:image/webp')
    avif = canvas.toDataURL('image/avif').startsWith('data:image/avif')
  } catch {
    // canvas indisponível — leave false
  }

  _cached = { avif, webp, intersectionObserver, serviceWorker, webWorker }
  return _cached
}
